
import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import AINoiseControl from './AINoiseControl';
import AISessionAssistant from './AISessionAssistant';
import SessionSummary from './SessionSummary';
import { Sparkles, FileCheck, Settings2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface AdvancedSessionToolsProps {
  sessionId: string;
  sessionName: string;
  sessionDuration: number;
  participantCount: number;
}

const AdvancedSessionTools: React.FC<AdvancedSessionToolsProps> = ({
  sessionId,
  sessionName,
  sessionDuration,
  participantCount
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>('assistant');
  const [isNoiseReductionEnabled, setIsNoiseReductionEnabled] = useState(true);
  const [noiseReductionLevel, setNoiseReductionLevel] = useState(65);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  
  const handleGenerateSummary = () => {
    setIsGeneratingSummary(true);
    
    toast({
      title: "Analyzing Session",
      description: "AI is processing session data to generate a summary.",
    });
    
    // Simulate AI processing time
    setTimeout(() => {
      setIsGeneratingSummary(false);
      
      toast({
        title: "Summary Generated",
        description: "AI has analyzed your session and created a detailed summary with action items."
      });
    }, 3000);
  };
  
  return (
    <div className="h-full flex flex-col">
      <Tabs 
        defaultValue={activeTab} 
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full h-full flex flex-col"
      >
        <TabsList className="grid grid-cols-3 mb-2 mx-2 mt-2">
          <TabsTrigger value="assistant" className="text-xs">
            <Sparkles className="h-3.5 w-3.5 mr-1" />
            Assistant
          </TabsTrigger>
          <TabsTrigger value="summary" className="text-xs">
            <FileCheck className="h-3.5 w-3.5 mr-1" />
            Summary
          </TabsTrigger>
          <TabsTrigger value="settings" className="text-xs">
            <Settings2 className="h-3.5 w-3.5 mr-1" />
            Settings
          </TabsTrigger>
        </TabsList>
        
        <div className="flex-1 overflow-hidden">
          <TabsContent value="assistant" className="m-0 h-full">
            <AISessionAssistant />
          </TabsContent>
          
          <TabsContent value="summary" className="m-0 h-full">
            <SessionSummary 
              sessionId={sessionId}
              sessionName={sessionName}
              sessionDuration={sessionDuration}
              participantCount={participantCount}
              isGenerating={isGeneratingSummary}
              onGenerateSummary={handleGenerateSummary}
            />
          </TabsContent>
          
          <TabsContent value="settings" className="m-0 p-3 h-full overflow-auto">
            <div className="space-y-6">
              <AINoiseControl 
                isEnabled={isNoiseReductionEnabled}
                onToggleNoiseReduction={setIsNoiseReductionEnabled}
                reductionLevel={noiseReductionLevel}
                onReductionLevelChange={setNoiseReductionLevel}
              />
              
              <div className="space-y-3 p-4 bg-black/20 rounded-lg border border-white/10">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-400" />
                  <h3 className="text-sm font-medium text-white">Other AI Features</h3>
                </div>
                
                <div className="text-xs text-white/70">
                  <p className="mb-2">The following features will be available in future updates:</p>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>Auto-Mix Suggestions</li>
                    <li>Real-Time Transcription</li>
                    <li>Emotion/Engagement Analysis</li>
                    <li>Automated Copyright Checks</li>
                    <li>DAW Integration (Logic Pro, Ableton)</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default AdvancedSessionTools;
